import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import {action, computed, makeAutoObservable, observable, reaction} from "mobx";
import {type IChecksums, type IChecksumStore} from "data/stores/checksum/checksum.store";
import type {IContestsStore} from "data/stores/contests/contests.store";
import type {ISquadsStore} from "data/stores/squads/squads.store";
import type {IRoundsStore} from "data/stores/rounds/rounds.store";

/**
 * Constant for determine update frequency.
 */
const LIVE_SCORING_FETCH_TIMING = 1000 * 20;

type IChecksumAction = Record<keyof IChecksums, () => Promise<void>>;

export interface ILiveScoreController extends ViewController {
	subscribeLiveScoring: (location?: string) => void;
	unsubscribeLiveScoring: () => void;
}

@injectable()
export class LiveScoreController implements ILiveScoreController {
	protected readonly _actions: IChecksumAction;
	@observable protected _interval?: ReturnType<typeof setInterval>;
	@observable protected _isSubscribed: boolean = false;
	@observable protected _liveScoreDisposer?: ReturnType<typeof reaction>;
	// @observable protected _changesDisposer?: ReturnType<typeof reaction>;

	get updatedChecksum(): IChecksums {
		return this._checksumStore.changedChecksums;
	}

	@computed
	private get changes() {
		return Object.keys(this._checksumStore.changedChecksums);
	}
	constructor(
		@inject(Bindings.ChecksumStore) private _checksumStore: IChecksumStore,
		@inject(Bindings.ContestsStore) private _contestsStore: IContestsStore,
		@inject(Bindings.SquadsStore) private _squadStore: ISquadsStore,
		@inject(Bindings.RoundsStore) private _roundsStore: IRoundsStore
	) {
		makeAutoObservable(this);
		this._actions = this.generateActions();
	}

	@action
	public subscribeLiveScoring() {
		if (this._isSubscribed) {
			return;
		}

		this._isSubscribed = true;

		void this._checksumStore.fetchChecksums();
		this.callActions();

		this._interval = setInterval(() => {
			void this._checksumStore.fetchChecksums();
			this.callActions();
		}, LIVE_SCORING_FETCH_TIMING);
	}

	/**
	 * Stop checking changes
	 * called on dispose
	 * or you can call it when you want to stop listen checksums, for example on the end of the game match/round/etc.
	 */
	@action
	public unsubscribeLiveScoring() {
		this._isSubscribed = false;

		if (this._interval) {
			clearInterval(this._interval);
		}
	}

	@action
	private callActions = () => {
		this.changes.forEach((key) => {
			const action = this._actions[key];

			if (action && typeof action === "function") {
				void action();
			}
		});
	};

	/**
	 * Provide object of files you want to update
	 * for example: rounds
	 */
	private generateActions(): IChecksumAction {
		return {
			fan_contests: () => this._contestsStore.fetchContest(),
			squads: () => this._squadStore.fetchSquads(),
			rounds: () => this._roundsStore.fetchRounds(),
		};
	}

	dispose(): void {
		this._liveScoreDisposer?.();
		this.unsubscribeLiveScoring();
	}

	async init() {
		await this._checksumStore.fetchChecksums();

		// Subscribe on changes
		this._liveScoreDisposer = reaction(() => this.updatedChecksum, this.callActions, {
			fireImmediately: true,
		});

		this.subscribeLiveScoring();
	}
}
